import { useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router";
import { toast } from "sonner";
import {
  ArrowLeft,
  Building2,
  CalendarClock,
  Check,
  ChevronRight,
  Mail,
  MessageCircle,
  Pencil,
  Phone,
  Plus,
  Trash2,
  UsersRound,
  X,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { ApiError } from "@/lib/api-client";
import { localizeError } from "@/lib/errorMessages";
import { ActivityTimeline } from "@/features/activity/activity-timeline";
import { formatRelativeVi } from "@/features/property/utils/relative-time";
import {
  useCustomer,
  useCustomerSummary,
  useDeleteCustomer,
  useUpdateCustomer,
} from "@/features/customer/api";
import { useTags } from "@/features/tag/api";
import { TAG_COLOR_SWATCH } from "@/features/tag/constants";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { CustomerSourceBadge } from "@/features/customer/components/customer-source-badge";
import { CustomerTagBadge } from "@/features/customer/components/customer-tag-badge";
import { CustomerEditSheet } from "@/features/customer/customer-edit-sheet";
import { LeadCreateForm } from "@/features/lead/components/lead-create-form";
import { useLeads } from "@/features/lead/api";
import { useAppointments } from "@/features/appointment/api";
import {
  LEAD_STATUS_DOT_COLORS,
  LEAD_STATUS_LABELS,
} from "@/features/lead/constants";
import { APPOINTMENT_STATUS_LABELS } from "@/features/appointment/constants";
import { useMediaQuery } from "@/features/lead/hooks/use-media-query";
import { initialsOf } from "@/features/customer/components/avatar-color";
import type { Customer, CustomerSummary } from "@/types/customer";
import type { Lead } from "@/features/lead/types";

interface CustomerDetailPanelProps {
  customerId: number | null;
  /** Clear the selection (desktop) or pop back to the list (mobile). */
  onClose?: () => void;
}

type TabKey = "activity" | "properties" | "appointments" | "leads";

// Single source of truth for the ORDER + labels of the stat cards and the
// content tabs. Both StatCards and TabBar render from this, so the two rows
// can never drift out of sync again (they were previously two independent
// arrays authored in different orders — cards had "Hoạt động" last, tabs had
// it first, so the highlighted card and tab sat in different positions).
const SECTIONS: Array<{
  key: TabKey;
  label: string;
  field: keyof CustomerSummary;
}> = [
  { key: "activity", label: "Hoạt động", field: "activities" },
  { key: "properties", label: "BĐS quan tâm", field: "interestedProperties" },
  { key: "appointments", label: "Lịch hẹn", field: "appointments" },
  { key: "leads", label: "Nhu cầu", field: "leads" },
];

export function CustomerDetailPanel({ customerId, onClose }: CustomerDetailPanelProps) {
  if (customerId === null) return <EmptyState />;
  return <Panel customerId={customerId} {...(onClose ? { onClose } : {})} />;
}

function Panel({
  customerId,
  onClose,
}: {
  customerId: number;
  onClose?: () => void;
}) {
  const navigate = useNavigate();
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const customerQuery = useCustomer(customerId);
  const summaryQuery = useCustomerSummary(customerId);
  const deleteMutation = useDeleteCustomer();

  const [tab, setTab] = useState<TabKey>("activity");
  const [editing, setEditing] = useState(false);
  const [creatingLead, setCreatingLead] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  if (customerQuery.isPending) return <PanelSkeleton />;
  if (customerQuery.isError || customerQuery.data === undefined) {
    const notFound =
      customerQuery.error instanceof ApiError && customerQuery.error.status === 404;
    return (
      <EmptyState
        title={notFound ? "Khách hàng không tồn tại" : "Không thể tải khách hàng"}
        description={
          notFound
            ? "Khách hàng này đã bị xoá hoặc không thuộc danh bạ của bạn."
            : "Vui lòng thử lại."
        }
      />
    );
  }

  const customer = customerQuery.data;
  const summary = summaryQuery.data;

  const handleDelete = () => {
    deleteMutation.mutate(customer.id, {
      onSuccess: () => {
        toast.success("Đã xóa khách hàng");
        setConfirmingDelete(false);
        onClose?.();
      },
      onError: (error) => toast.error(localizeError(error)),
    });
  };

  return (
    <div className="flex h-full flex-col overflow-y-auto">
      {/* Mobile back bar */}
      {!isDesktop && onClose ? (
        <button
          type="button"
          onClick={onClose}
          className="flex items-center gap-1.5 border-b border-border px-4 py-3 text-sm text-muted-foreground"
        >
          <ArrowLeft aria-hidden="true" className="size-4" />
          Danh sách
        </button>
      ) : null}

      <div className="flex flex-col gap-5 p-5 sm:p-6">
        <CustomerHeader
          customer={customer}
          onEdit={() => setEditing(true)}
          onDelete={() => setConfirmingDelete(true)}
          onCreateLead={() => setCreatingLead(true)}
        />

        {customer.notes ? <NotesCard customer={customer} /> : null}

        <StatCards
          summary={
            summary ?? {
              interestedProperties: 0,
              leads: 0,
              appointments: 0,
              activities: 0,
            }
          }
          loading={summaryQuery.isPending}
          active={tab}
          onSelect={setTab}
        />

        <div>
          <TabBar tab={tab} onSelect={setTab} summary={summary} />
          <div className="pt-4">
            {tab === "activity" ? (
              <ActivityTimeline entityType="CUSTOMER" entityId={customer.id} />
            ) : tab === "properties" ? (
              <InterestedPropertiesTab customerId={customer.id} />
            ) : tab === "appointments" ? (
              <AppointmentsTab customerId={customer.id} />
            ) : (
              <LeadsTab customerId={customer.id} />
            )}
          </div>
        </div>
      </div>

      <CustomerEditSheet
        customer={editing ? customer : null}
        onClose={() => setEditing(false)}
      />

      <CreateLeadSurface
        customer={creatingLead ? customer : null}
        isDesktop={isDesktop}
        onClose={() => setCreatingLead(false)}
        onCreated={(lead) => {
          setCreatingLead(false);
          toast.success("Đã tạo nhu cầu mới");
          navigate(`/leads/${lead.id}`);
        }}
      />

      <Dialog
        open={confirmingDelete}
        onOpenChange={(o) => (!o ? setConfirmingDelete(false) : undefined)}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Xóa khách hàng?</DialogTitle>
            <DialogDescription>
              Hành động này sẽ xóa &quot;{customer.fullName}&quot;. Bạn có chắc?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button
              variant="outline"
              onClick={() => setConfirmingDelete(false)}
              disabled={deleteMutation.isPending}
            >
              Hủy
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
            >
              <Trash2 aria-hidden="true" />
              Xóa khách hàng
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CustomerHeader({
  customer,
  onEdit,
  onDelete,
  onCreateLead,
}: {
  customer: Customer;
  onEdit: () => void;
  onDelete: () => void;
  onCreateLead: () => void;
}) {
  return (
    <header className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-4">
          <div
            aria-hidden="true"
            className="flex size-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-primary to-foreground text-lg font-bold text-primary-foreground shadow-[var(--shadow-soft)]"
          >
            {initialsOf(customer.fullName)}
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-xl font-bold tracking-tight sm:text-2xl">
                {customer.fullName}
              </h2>
              <CustomerTagEditor customer={customer} />
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[14px]">
              <a
                href={`tel:${customer.phoneNumber}`}
                className="inline-flex items-center gap-1 font-mono text-primary hover:underline"
              >
                {customer.phoneNumber}
              </a>
              {customer.email ? (
                <a
                  href={`mailto:${customer.email}`}
                  className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
                >
                  {customer.email}
                </a>
              ) : null}
              <CustomerSourceBadge source={customer.source} />
            </div>
          </div>
        </div>
        {/* Edit / delete toolbar */}
        <div className="flex shrink-0 items-center gap-1">
          <IconButton label="Sửa" onClick={onEdit}>
            <Pencil aria-hidden="true" className="size-4" />
          </IconButton>
          <IconButton label="Xóa" onClick={onDelete} destructive>
            <Trash2 aria-hidden="true" className="size-4" />
          </IconButton>
        </div>
      </div>

      {/* Quick actions */}
      <div className="flex flex-wrap items-center gap-2">
        <ActionLink href={`tel:${customer.phoneNumber}`} label="Gọi">
          <Phone aria-hidden="true" className="size-4" />
        </ActionLink>
        <ActionLink href={`sms:${customer.phoneNumber}`} label="Nhắn tin">
          <MessageCircle aria-hidden="true" className="size-4" />
        </ActionLink>
        {customer.email ? (
          <ActionLink href={`mailto:${customer.email}`} label="Email">
            <Mail aria-hidden="true" className="size-4" />
          </ActionLink>
        ) : null}
        <Button onClick={onCreateLead} className="ml-auto h-9">
          <Plus aria-hidden="true" />
          Tạo nhu cầu
        </Button>
      </div>
    </header>
  );
}

/** The customer's tag badges + an inline "+ Nhãn" picker to toggle org tags. */
function CustomerTagEditor({ customer }: { customer: Customer }) {
  const [open, setOpen] = useState(false);
  const tagsQuery = useTags();
  const update = useUpdateCustomer(customer.id);
  const orgTags = tagsQuery.data ?? [];
  const currentIds = new Set(customer.tags.map((t) => t.id));

  const toggle = (tagId: number) => {
    const next = currentIds.has(tagId)
      ? [...currentIds].filter((id) => id !== tagId)
      : [...currentIds, tagId];
    update.mutate({ tagIds: next }, { onError: (e) => toast.error(localizeError(e)) });
  };

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {customer.tags.map((t) => (
        <CustomerTagBadge key={t.id} tag={t} />
      ))}
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label="Gắn nhãn"
            className="inline-flex items-center gap-1 rounded-md border border-dashed border-border px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <Plus aria-hidden="true" className="size-3" />
            Nhãn
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-52 p-1">
          {orgTags.length === 0 ? (
            <p className="px-2 py-3 text-center text-[12px] text-muted-foreground">
              Chưa có nhãn. Tạo trong Cài đặt → Nhãn.
            </p>
          ) : (
            orgTags.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => toggle(t.id)}
                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[12px] hover:bg-muted"
              >
                <span className={cn("size-2.5 shrink-0 rounded-full", TAG_COLOR_SWATCH[t.color])} />
                <span className="flex-1 truncate">{t.name}</span>
                {currentIds.has(t.id) ? (
                  <Check aria-hidden="true" className="size-3.5 text-primary" />
                ) : null}
              </button>
            ))
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

function NotesCard({ customer }: { customer: Customer }) {
  return (
    <section className="flex flex-col gap-1.5 rounded-xl border border-border bg-card p-4 shadow-[var(--shadow-soft)]">
      <div className="flex items-center justify-between">
        <h3 className="text-[12px] font-semibold">Ghi chú khách hàng</h3>
        <span className="font-mono text-[11px] text-ink-faint">
          cập nhật {formatRelativeVi(customer.updatedAt)}
        </span>
      </div>
      <p className="whitespace-pre-wrap text-[14px] leading-relaxed text-ink-soft">
        {customer.notes}
      </p>
    </section>
  );
}

function StatCards({
  summary,
  loading,
  active,
  onSelect,
}: {
  summary: CustomerSummary;
  loading: boolean;
  active: TabKey;
  onSelect: (t: TabKey) => void;
}) {
  return (
    <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
      {SECTIONS.map((c) => (
        <button
          key={c.key}
          type="button"
          onClick={() => onSelect(c.key)}
          className={cn(
            "flex flex-col gap-1 rounded-xl border bg-card p-3 text-left shadow-[var(--shadow-soft)] transition-colors",
            active === c.key
              ? "border-primary/40 bg-primary/5"
              : "border-border hover:bg-muted/40",
          )}
        >
          <span className="text-2xl font-bold tracking-tight tabular-nums">
            {loading ? "—" : summary[c.field]}
          </span>
          <span className="text-[12px] text-muted-foreground">{c.label}</span>
        </button>
      ))}
    </div>
  );
}

function TabBar({
  tab,
  onSelect,
  summary,
}: {
  tab: TabKey;
  onSelect: (t: TabKey) => void;
  summary: CustomerSummary | undefined;
}) {
  return (
    <div className="flex gap-4 overflow-x-auto border-b border-border">
      {SECTIONS.map((t) => {
        const count = summary?.[t.field];
        return (
          <button
            key={t.key}
            type="button"
            onClick={() => onSelect(t.key)}
            className={cn(
              "flex shrink-0 items-center gap-1.5 border-b-2 pb-2.5 text-[14px] font-medium transition-colors",
              tab === t.key
                ? "border-primary text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
          >
            {t.label}
            {count !== undefined ? (
              <span className="font-mono text-[11px] text-ink-faint">{count}</span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

function InterestedPropertiesTab({ customerId }: { customerId: number }) {
  const leadsQuery = useLeads({ customerId });
  if (leadsQuery.isPending) return <RowsSkeleton />;
  const leads = leadsQuery.data ?? [];
  // Distinct properties across the customer's leads.
  const seen = new Set<number>();
  const properties = leads
    .filter((l) => l.interestedPropertyId !== null)
    .filter((l) => {
      const id = l.interestedPropertyId as number;
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  if (properties.length === 0) {
    return <TabEmpty icon={<Building2 className="size-5" />} text="Chưa có BĐS quan tâm." />;
  }
  return (
    <ul className="flex flex-col gap-2">
      {properties.map((l) => (
        <li key={l.interestedPropertyId}>
          <Link
            to={`/properties/${l.interestedPropertyId}`}
            className="flex items-center gap-3 rounded-lg border border-border bg-card p-3 transition-colors hover:bg-muted/40"
          >
            <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted text-ink-soft">
              <Building2 className="size-4" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-[14px] font-semibold">
                {l.interestedPropertyBuildingName ??
                  l.interestedPropertyInternalCode ??
                  `BĐS #${l.interestedPropertyId}`}
              </div>
              {l.interestedPropertyInternalCode ? (
                <div className="font-mono text-[11px] text-ink-faint">
                  {l.interestedPropertyInternalCode}
                </div>
              ) : null}
            </div>
            <ChevronRight className="size-3.5 shrink-0 text-ink-faint" />
          </Link>
        </li>
      ))}
    </ul>
  );
}

function LeadsTab({ customerId }: { customerId: number }) {
  const leadsQuery = useLeads({ customerId });
  if (leadsQuery.isPending) return <RowsSkeleton />;
  const leads = leadsQuery.data ?? [];
  if (leads.length === 0) {
    return <TabEmpty icon={<UsersRound className="size-5" />} text="Chưa có nhu cầu nào." />;
  }
  return (
    <ul className="flex flex-col gap-2">
      {leads.map((l: Lead) => (
        <li key={l.id}>
          <Link
            to={`/leads/${l.id}`}
            className="flex items-center gap-3 rounded-lg border border-border bg-card p-3 transition-colors hover:bg-muted/40"
          >
            <span
              aria-hidden="true"
              className={cn(
                "size-2 shrink-0 rounded-full",
                LEAD_STATUS_DOT_COLORS[l.status],
              )}
            />
            <div className="min-w-0 flex-1">
              <div className="truncate text-[14px] font-semibold">
                {l.interestedPropertyBuildingName ??
                  l.interestedPropertyInternalCode ??
                  "Chưa gắn BĐS"}
              </div>
              <div className="text-[12px] text-muted-foreground">
                {LEAD_STATUS_LABELS[l.status]}
              </div>
            </div>
            <ChevronRight className="size-3.5 shrink-0 text-ink-faint" />
          </Link>
        </li>
      ))}
    </ul>
  );
}

function AppointmentsTab({ customerId }: { customerId: number }) {
  const apptQuery = useAppointments({ customerId });
  if (apptQuery.isPending) return <RowsSkeleton />;
  const appts = apptQuery.data ?? [];
  if (appts.length === 0) {
    return <TabEmpty icon={<CalendarClock className="size-5" />} text="Chưa có lịch hẹn nào." />;
  }
  return (
    <ul className="flex flex-col gap-2">
      {appts.map((a) => (
        <li key={a.id}>
          <Link
            to={`/appointments/${a.id}`}
            className="flex items-center gap-3 rounded-lg border border-border bg-card p-3 transition-colors hover:bg-muted/40"
          >
            <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted text-ink-soft">
              <CalendarClock className="size-4" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-[14px] font-semibold">
                {a.scheduledAt
                  ? new Date(a.scheduledAt).toLocaleString("vi-VN", {
                      day: "2-digit",
                      month: "2-digit",
                      hour: "2-digit",
                      minute: "2-digit",
                    })
                  : "Chưa chốt giờ"}
              </div>
              <div className="text-[12px] text-muted-foreground">
                {APPOINTMENT_STATUS_LABELS[a.status]}
              </div>
            </div>
            <ChevronRight className="size-3.5 shrink-0 text-ink-faint" />
          </Link>
        </li>
      ))}
    </ul>
  );
}

// ---- small shared bits ----

function IconButton({
  children,
  label,
  onClick,
  destructive,
}: {
  children: ReactNode;
  label: string;
  onClick: () => void;
  destructive?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className={cn(
        "flex size-8 items-center justify-center rounded-lg border border-border bg-background text-muted-foreground transition-colors hover:bg-muted",
        destructive ? "hover:text-destructive" : "hover:text-foreground",
      )}
    >
      {children}
    </button>
  );
}

function ActionLink({
  href,
  label,
  children,
}: {
  href: string;
  label: string;
  children: ReactNode;
}) {
  return (
    <a
      href={href}
      aria-label={label}
      className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-2 text-[12px] font-medium text-ink-soft shadow-[var(--shadow-soft)] transition-colors hover:bg-muted/60 hover:text-foreground"
    >
      {children}
      <span>{label}</span>
    </a>
  );
}

function TabEmpty({ icon, text }: { icon: ReactNode; text: string }) {
  return (
    <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-border py-10 text-center text-muted-foreground">
      <span className="text-ink-faint">{icon}</span>
      <p className="text-[12px]">{text}</p>
    </div>
  );
}

function RowsSkeleton() {
  return (
    <div className="flex flex-col gap-2" aria-busy="true">
      {Array.from({ length: 3 }).map((_, i) => (
        <Skeleton key={i} className="h-14 w-full rounded-lg" />
      ))}
    </div>
  );
}

function PanelSkeleton() {
  return (
    <div className="flex flex-col gap-5 p-5 sm:p-6" aria-busy="true">
      <div className="flex items-start gap-4">
        <Skeleton className="size-14 rounded-2xl" />
        <div className="flex flex-1 flex-col gap-2">
          <Skeleton className="h-6 w-1/2" />
          <Skeleton className="h-4 w-2/3" />
        </div>
      </div>
      <Skeleton className="h-20 w-full rounded-xl" />
      <div className="grid grid-cols-4 gap-2.5">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-16 rounded-xl" />
        ))}
      </div>
      <Skeleton className="h-40 w-full" />
    </div>
  );
}

function EmptyState({
  title = "Chọn một khách hàng",
  description = "Nhấp vào khách hàng trong danh sách để xem chi tiết.",
}: {
  title?: string;
  description?: string;
} = {}) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-center">
      <div className="flex size-12 items-center justify-center rounded-2xl bg-muted text-ink-faint">
        <UsersRound className="size-5" />
      </div>
      <h3 className="text-sm font-semibold text-foreground">{title}</h3>
      <p className="max-w-xs text-xs text-muted-foreground">{description}</p>
    </div>
  );
}

interface CreateLeadSurfaceProps {
  customer: Customer | null;
  isDesktop: boolean;
  onClose: () => void;
  onCreated: (lead: Lead) => void;
}

/**
 * Customer-locked lead-create form, in a Dialog (desktop) / Sheet (mobile).
 *
 * Mirrors the kanban `LeadCreateSurface` shell: a flush (`p-0 gap-0`),
 * 860px-wide modal with a custom header, because `LeadCreateForm` has a
 * two-column body (`md:grid-cols-[1.4fr_1fr]`) plus its own padding and
 * footer. A narrower/padded shell makes the right column overflow the dialog.
 */
function CreateLeadSurface({ customer, isDesktop, onClose, onCreated }: CreateLeadSurfaceProps) {
  if (customer === null) return null;
  const body = (
    <LeadCreateForm
      mode={{ kind: "customer-locked", prefilledCustomer: customer }}
      onSuccess={onCreated}
      onCancel={onClose}
    />
  );
  const eyebrow = (
    <p className="text-[12px] text-muted-foreground">Mới · Nhu cầu</p>
  );
  const closeButton = (
    <button
      type="button"
      onClick={onClose}
      aria-label="Đóng"
      className="flex size-8 items-center justify-center rounded-lg border border-border bg-background text-muted-foreground hover:bg-muted"
    >
      <X aria-hidden="true" className="size-3.5" />
    </button>
  );
  if (isDesktop) {
    return (
      <Dialog open onOpenChange={(o) => (!o ? onClose() : undefined)}>
        <DialogContent
          className="max-h-[88vh] gap-0 overflow-y-auto p-0 sm:max-w-[860px]"
          showCloseButton={false}
        >
          <div className="flex items-center justify-between border-b border-border px-5 py-4">
            <div>
              {eyebrow}
              <DialogTitle className="mt-0.5 text-[16px] font-bold tracking-tight">
                Tạo nhu cầu mới
              </DialogTitle>
              <DialogDescription className="sr-only">
                Tạo nhu cầu mới cho khách hàng {customer.fullName}
              </DialogDescription>
            </div>
            <div className="flex items-center gap-2">
              <span
                aria-hidden="true"
                className="inline-flex h-6 items-center rounded-md border border-border bg-muted/60 px-1.5 font-mono text-[11px] font-semibold text-ink-soft"
              >
                Esc
              </span>
              {closeButton}
            </div>
          </div>
          {body}
        </DialogContent>
      </Dialog>
    );
  }
  return (
    <Sheet open onOpenChange={(o) => (!o ? onClose() : undefined)}>
      <SheetContent
        side="bottom"
        className="max-h-[90vh] gap-0 overflow-y-auto p-0"
        showCloseButton={false}
      >
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <div>
            {eyebrow}
            <SheetTitle className="mt-0.5 text-[16px] font-bold tracking-tight">
              Tạo nhu cầu mới
            </SheetTitle>
            <SheetDescription className="sr-only">
              Tạo nhu cầu mới cho khách hàng {customer.fullName}
            </SheetDescription>
          </div>
          {closeButton}
        </div>
        {body}
      </SheetContent>
    </Sheet>
  );
}
